import React, { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { ThumbUp, ThumbDown } from '@/assets/icons';
import asthaIcon from '@/assets/images/aa-astha-icon.png';
import prakruthiIcon from '@/assets/images/aa-prakruthi-icon.png';
import doshaIcon from '@/assets/images/aa-dosha-icon.png';
import aamaIcon from '@/assets/images/aa-aama-icon.png';
import headerIcon from '@/assets/images/aa-header-icon.png';
import { ClinicalAssessmentChipGroup } from './ClinicalAssessmentChipGroup';
import {
  NADI_OPTIONS,
  MOOTRAM_OPTIONS,
  MALAM_OPTIONS,
  JIHVA_OPTIONS,
  SABDAM_OPTIONS,
  SPARSHAM_OPTIONS,
  DRIK_OPTIONS,
  AKRITHI_OPTIONS,
  PRAKRUTHI_DOSHA_OPTIONS,
  AAMA_OPTIONS,
} from '@/ayurveda/config/clinicalAssessmentOptions';
import type { ClinicalAssessmentEntry, ClinicalAssessmentParam } from '@/ayurveda/types/ayurveda';

export interface AyurvedicAssessmentCardProps {
  entries: ClinicalAssessmentEntry[];
  onOverride?: (parameter: ClinicalAssessmentParam, value: string) => void;
  pendingParameter?: ClinicalAssessmentParam | null;
}

const ASTHA_STHAANA_GROUPS: Array<{
  parameter: ClinicalAssessmentParam;
  label: string;
  options: readonly string[];
}> = [
  { parameter: 'nadi', label: 'Nadi', options: NADI_OPTIONS },
  { parameter: 'mootram', label: 'Mootram', options: MOOTRAM_OPTIONS },
  { parameter: 'malam', label: 'Malam', options: MALAM_OPTIONS },
  { parameter: 'jihva', label: 'Jihva', options: JIHVA_OPTIONS },
  { parameter: 'sabdam', label: 'Sabdam', options: SABDAM_OPTIONS },
  { parameter: 'sparsham', label: 'Sparsham', options: SPARSHAM_OPTIONS },
  { parameter: 'drik', label: 'Drik', options: DRIK_OPTIONS },
  { parameter: 'akrithi', label: 'Akrithi', options: AKRITHI_OPTIONS },
];

function SectionHeader({
  icon,
  badgeColor,
  children,
}: {
  icon: string;
  badgeColor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1">
      <div
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: badgeColor }}
      >
        <img src={icon} alt="" className="h-5 w-5" aria-hidden="true" />
      </div>
      <p className="font-hanken text-lg font-bold text-[#585859]">{children}</p>
    </div>
  );
}

export function AyurvedicAssessmentCard({
  entries,
  onOverride,
  pendingParameter,
}: AyurvedicAssessmentCardProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="mb-4 flex flex-col gap-3 rounded-2xl bg-[#F8F8F1] p-4">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2">
          <img src={headerIcon} alt="" className="h-5 w-5" aria-hidden="true" />
          <h2 className="font-hanken text-[calc(20px*var(--app-scale))] font-medium leading-6 tracking-[0px] text-[#3C4F1E]">
            Ayurvedic Assessment
          </h2>
        </div>
        {isOpen ? (
          <ChevronUp size={18} className="text-[#8D90A1]" />
        ) : (
          <ChevronDown size={18} className="text-[#8D90A1]" />
        )}
      </button>

      {isOpen && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3 rounded-xl bg-white px-3 pb-2 pt-1">
            <SectionHeader icon={asthaIcon} badgeColor="#F1F5EE">
              Astha Sthaana Pareeksha:
            </SectionHeader>
            <div className="flex flex-col gap-3">
              {ASTHA_STHAANA_GROUPS.map((group) => (
                <ClinicalAssessmentChipGroup
                  key={group.parameter}
                  label={group.label}
                  parameter={group.parameter}
                  options={group.options}
                  color="sage"
                  entries={entries}
                  onOverride={onOverride}
                  pending={pendingParameter === group.parameter}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-xl bg-white px-3 pb-2 pt-1">
            <SectionHeader icon={prakruthiIcon} badgeColor="#F6ECFB">
              Prakruthi:
            </SectionHeader>
            <ClinicalAssessmentChipGroup
              label=""
              parameter="prakruthi"
              options={PRAKRUTHI_DOSHA_OPTIONS}
              color="purple"
              entries={entries}
              onOverride={onOverride}
              pending={pendingParameter === 'prakruthi'}
            />
          </div>

          <div className="flex flex-col gap-3 rounded-xl bg-white px-3 pb-2 pt-1">
            <SectionHeader icon={doshaIcon} badgeColor="#F8F4EA">
              Dosha:
            </SectionHeader>
            <ClinicalAssessmentChipGroup
              label=""
              parameter="dosha"
              options={PRAKRUTHI_DOSHA_OPTIONS}
              color="gold"
              entries={entries}
              onOverride={onOverride}
              pending={pendingParameter === 'dosha'}
            />
          </div>

          <div className="flex flex-col gap-3 rounded-xl bg-white px-3 py-2">
            <SectionHeader icon={aamaIcon} badgeColor="#FEEAE5">
              Aama:
            </SectionHeader>
            <ClinicalAssessmentChipGroup
              label=""
              parameter="aama"
              options={AAMA_OPTIONS}
              color="coral"
              entries={entries}
              onOverride={onOverride}
              pending={pendingParameter === 'aama'}
            />
          </div>

          <div className="flex items-center gap-2 px-1">
            <ThumbUp size={16} fill="#717072" />
            <ThumbDown size={16} fill="#717072" />
          </div>
        </div>
      )}
    </div>
  );
}
